import { v4 as uuidv4 } from "uuid";
import {
  DateInput,
  TextArea,
  TextInput,
  WithErrorForm,
} from "components/common";
import { Modal, ModalProps } from "components/common/Modal";
import { Button } from "components/parts/Button/Button";
import { SelectBox } from "components/parts/Input/SelectBox";
import { Option } from "components/parts/Input/SelectBox/SelectBox";
import { Typography } from "components/parts/Typography/Typography";
import { setNestedObjectValues, useFormik } from "formik";
import { DisplaySubject } from "pages/SubjectManagement/hook";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import {
  CreateMarkedSubjectInput,
  CreateSubjectInput,
  DeleteSubjectInput,
  Interview,
  InterviewType,
  UpdateSubjectInput,
} from "types/graphqlapi";
import {
  createSubjectMutation,
  deleteSubjectMutation,
  updateSubjectMutation,
} from "utils/apis/subject";
import { validationSchemaSubject } from "utils/validation";

import {
  StyledButtonArea,
  StyledInterviewTypeWrapper,
  StyledRedTypography,
} from "./styled";
import {
  StyledForm,
  StyledFormRow,
  StyledModalContent,
  StyledModalFooter,
  StyledModalHeader,
  StyledVerticalArea,
} from "./styled";
import { RadioButton } from "components/common/RadioButton";
import { listInterviewsQuery } from "utils/apis/interview";
import { createMarkedSubjectMutation } from "utils/apis/markedSubject";
import { getYMDStr } from "utils/common";

type SubjectManageModalProps = {
  selectedSubject: DisplaySubject;
  clearSelectedSubject: () => void;
  optionGroupUserList: Option[];
  asmarqUserList: Option[];
  optionGroupList: Option[];
} & ModalProps;

type SubjectFormValueType = {
  group_id: string;
  subject_number: string;
  subject_name: string;
  interview_type: InterviewType;
  subject_description: string;
  subject_purpose: string;
  main_user_id_group: string;
  main_user_id_asmarq: string;
  canceled: boolean;
  memo: string;
  input_expiration_date?: Date;
};

export const SubjectManageModal: React.FC<SubjectManageModalProps> = ({
  closeHandler,
  selectedSubject,
  clearSelectedSubject,
  optionGroupUserList,
  asmarqUserList,
  optionGroupList,
}) => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const { selectedGroupId, isOneGroupUser, subjectFilter, userId } =
    useConsoleState();
  const [interviewList, setInterviewList] = React.useState<Interview[]>([]);

  // フォームの入力値からユーザーのロールを決定する
  const cancelMapper = (canceled: boolean): number => {
    if (canceled) return 1;
    return 0;
  };

  const unselectedDate = new Date(2999, 11, 31, 23, 59, 59);

  // TODO: subjectSeq
  const insertSubject = async (
    values: SubjectFormValueType
  ): Promise<string | void> => {
    const createSubjectInput: CreateSubjectInput = {
      group_id: isOneGroupUser ? selectedGroupId : values.group_id,
      subject_seq: 1,
      subject_number: values.subject_number,
      subject_name: values.subject_name,
      interview_type: values.interview_type,
      subject_description: values.subject_description,
      subject_purpose: values.subject_purpose,
      input_expiration_date: values.input_expiration_date
        ? getYMDStr(values.input_expiration_date)
        : undefined,
      expiration_date: values.input_expiration_date
        ? getYMDStr(values.input_expiration_date)
        : getYMDStr(unselectedDate),
      main_user_id_group: values.main_user_id_group,
      main_user_id_asmarq: values.main_user_id_asmarq,
      memo: values.memo,
      canceled: 0,
      groups: isOneGroupUser ? selectedGroupId : values.group_id,
    };
    const createdSubjectId = await createSubjectMutation(createSubjectInput);

    return createdSubjectId;
  };

  const updateSubject = async (values: SubjectFormValueType): Promise<void> => {
    const updateSubjectInput: UpdateSubjectInput = {
      id: selectedSubject.id,
      subject_number: values.subject_number,
      subject_name: values.subject_name,
      interview_type: values.interview_type,
      subject_description: values.subject_description,
      subject_purpose: values.subject_purpose,
      input_expiration_date: values.input_expiration_date
        ? getYMDStr(values.input_expiration_date)
        : undefined,
      expiration_date: values.input_expiration_date
        ? getYMDStr(values.input_expiration_date)
        : selectedSubject.expiration_date,
      main_user_id_group: values.main_user_id_group,
      main_user_id_asmarq: values.main_user_id_asmarq,
      canceled: cancelMapper(values.canceled),
      memo: values.memo,
    };
    await updateSubjectMutation(updateSubjectInput);
  };

  const clickDeleteButtonHandler: React.FormEventHandler<
    HTMLButtonElement
  > = async () => {
    const res = confirm("選択された案件を削除します");
    if (res) {
      try {
        setLoading(true);
        const deleteSubjectInput: DeleteSubjectInput = {
          id: selectedSubject.id,
        };
        await deleteSubjectMutation(deleteSubjectInput);
        // TOTO カスケードしての削除
        clearSelectedSubject();
      } catch (e) {
        console.error(e);
        alert("エラーが発生しました。");
        setLoading(false);
        history.replace("/auth/login");
      }
    }
  };

  // submit時に判断・分岐するのが難しいため「スター付き登録」用の関数を定義
  const clickMarkedInsertButtonHandler: React.FormEventHandler<
    HTMLButtonElement
  > = async () => {
    // 参考：https://github.com/jaredpalmer/formik/issues/2734#issuecomment-923337541
    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      formik.setTouched(setNestedObjectValues(validationErrors, true));
      return;
    }

    try {
      setLoading(true);

      const createdSubjectId = await insertSubject(formik.values);
      if (!createdSubjectId) return;

      const markedSubjectInput: CreateMarkedSubjectInput = {
        id: uuidv4(),
        user_id: userId,
        subject_id: createdSubjectId,
        groups: isOneGroupUser ? selectedGroupId : formik.values.group_id,
      };

      await createMarkedSubjectMutation(markedSubjectInput);
      // // TOTO カスケードしての削除
      clearSelectedSubject();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const onSubmit = async (values: SubjectFormValueType) => {
    try {
      setLoading(true);

      selectedSubject.id
        ? await updateSubject(values)
        : await insertSubject(values);
      clearSelectedSubject();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const initValues: SubjectFormValueType = {
    group_id: isOneGroupUser
      ? selectedGroupId
      : selectedSubject.group_id
      ? selectedSubject.group_id
      : subjectFilter.group !== "ALL" && subjectFilter.isSelectGroup
      ? subjectFilter.group
      : "",
    subject_number: selectedSubject.subject_number || "",
    subject_name: selectedSubject.subject_name || "",
    interview_type: selectedSubject.interview_type || InterviewType.ONE_TIME,
    subject_description: selectedSubject.subject_description || "",
    subject_purpose: selectedSubject.subject_purpose || "",
    input_expiration_date: selectedSubject.input_expiration_date
      ? new Date(selectedSubject.input_expiration_date)
      : undefined,
    main_user_id_group: selectedSubject.main_user_id_group || "",
    main_user_id_asmarq: selectedSubject.main_user_id_asmarq || "",
    canceled: selectedSubject.canceled ? true : false,
    memo: selectedSubject.memo || "",
  };

  const formik = useFormik<SubjectFormValueType>({
    initialValues: initValues,
    validationSchema: validationSchemaSubject(interviewList),
    onSubmit: onSubmit,
  });

  // 非同期通信の結果次第で画面表示が変わるので通信をしている間は初期状態を空にしておきたい
  type InterviewTypeEditableType = "NONE" | "EDITABLE" | "NOT_EDITABLE";

  const [interviewTypeEditable, setInterviewTypeEditable] =
    React.useState<InterviewTypeEditableType>("NONE");

  const latestEndDate =
    interviewList.length !== 0
      ? interviewList
          .map((interview) => new Date(interview.scheduled_end_date as string))
          .reduce((latestDate, currentDate) =>
            latestDate > currentDate ? latestDate : currentDate
          )
      : undefined;

  const minDate = latestEndDate ? latestEndDate : new Date();
  minDate.setDate(minDate.getDate() + 1);

  React.useEffect(() => {
    (async () => {
      if (!selectedSubject.id) {
        setInterviewTypeEditable("EDITABLE");
      } else {
        const interviewList = await listInterviewsQuery(selectedSubject.id);
        setInterviewTypeEditable(
          interviewList?.length === 0 ? "EDITABLE" : "NOT_EDITABLE"
        );
        setInterviewList(interviewList);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal closeHandler={closeHandler}>
      <StyledForm onSubmit={formik.handleSubmit}>
        <StyledModalHeader>
          {selectedSubject.id ? (
            <Typography text="案件編集" size="large" />
          ) : (
            <Typography text="新規案件追加" size="large" />
          )}
        </StyledModalHeader>

        <StyledModalContent>
          <StyledVerticalArea>
            {isOneGroupUser ? (
              <></>
            ) : (
              <StyledFormRow>
                <Typography text="グループ名" required={!selectedSubject.id} />
                <WithErrorForm
                  touched={formik.touched.group_id}
                  errorMessage={formik.errors.group_id}
                >
                  <SelectBox
                    autoFocus={!selectedSubject.id && !isOneGroupUser}
                    name="group_id"
                    value={formik.values.group_id}
                    optionList={[
                      { value: "", text: "---" },
                      ...optionGroupList,
                    ]}
                    width="20rem"
                    onBlur={() =>
                      formik.setFieldTouched("group_id", true, true)
                    }
                    onChange={(e) => {
                      formik.setFieldValue("group_id", e.target.value);
                    }}
                    disabled={selectedSubject.id !== undefined}
                  />
                </WithErrorForm>
              </StyledFormRow>
            )}

            <StyledFormRow>
              <Typography text="案件ID" required />
              <WithErrorForm
                touched={formik.touched.subject_number}
                errorMessage={formik.errors.subject_number}
              >
                <TextInput
                  autoFocus={isOneGroupUser || !!selectedSubject.id}
                  {...formik.getFieldProps("subject_number")}
                  width="8rem"
                  maxlength="7"
                />
              </WithErrorForm>
            </StyledFormRow>

            <StyledFormRow>
              <Typography text="案件名" required />
              <WithErrorForm
                touched={formik.touched.subject_name}
                errorMessage={formik.errors.subject_name}
              >
                <TextInput
                  {...formik.getFieldProps("subject_name")}
                  width="20rem"
                  maxlength="30"
                />
              </WithErrorForm>
            </StyledFormRow>

            <StyledFormRow>
              <StyledFormRow>
                <Typography
                  text="ルームタイプ"
                  required={!selectedSubject.id}
                />
                <StyledInterviewTypeWrapper>
                  {interviewTypeEditable === "NONE" ? (
                    <Typography text="" />
                  ) : interviewTypeEditable === "EDITABLE" ? (
                    <>
                      <RadioButton
                        {...formik.getFieldProps("interview_type")}
                        id="ONE_TIME"
                        name="interview_type"
                        value={InterviewType.ONE_TIME}
                        text="ワンタイム"
                        checked={
                          formik.values.interview_type ===
                          InterviewType.ONE_TIME
                        }
                        onChange={(e) =>
                          formik.setFieldValue("interview_type", e.target.value)
                        }
                      />
                      <RadioButton
                        {...formik.getFieldProps("interview_type")}
                        id="TERM"
                        name="interview_type"
                        value={InterviewType.TERM}
                        text="期間指定"
                        checked={
                          formik.values.interview_type === InterviewType.TERM
                        }
                        onChange={(e) =>
                          formik.setFieldValue("interview_type", e.target.value)
                        }
                      />
                    </>
                  ) : selectedSubject.interview_type === InterviewType.TERM ? (
                    <Typography text="期間指定" />
                  ) : (
                    <Typography text="ワンタイム" />
                  )}
                </StyledInterviewTypeWrapper>
              </StyledFormRow>
            </StyledFormRow>

            <StyledFormRow>
              <Typography text="データ削除日" />
              <WithErrorForm
                touched={formik.touched.input_expiration_date}
                errorMessage={formik.errors.input_expiration_date}
              >
                <DateInput
                  name="input_expiration_date"
                  date={
                    formik.values.input_expiration_date
                      ? new Date(formik.values.input_expiration_date)
                      : undefined
                  }
                  onChangeHandler={(date) =>
                    formik.setFieldValue("input_expiration_date", date)
                  }
                  onBlurHandler={() =>
                    formik.setFieldTouched("input_expiration_date", true, true)
                  }
                  width="10rem"
                  minDate={minDate}
                />
              </WithErrorForm>
            </StyledFormRow>
            <StyledRedTypography
              text="※指定がなければ、最終実査日の90日後で自動反映"
              size="xsmall"
            />

            <StyledFormRow>
              <Typography text="案件概要" />
              <WithErrorForm
                touched={formik.touched.subject_description}
                errorMessage={formik.errors.subject_description}
              >
                <TextArea
                  {...formik.getFieldProps("subject_description")}
                  width="20rem"
                  height={isOneGroupUser ? "7.5rem" : "6rem"}
                  maxlength="150"
                />
              </WithErrorForm>
            </StyledFormRow>

            <StyledFormRow>
              <Typography text="調査目的" />
              <WithErrorForm
                touched={formik.touched.subject_purpose}
                errorMessage={formik.errors.subject_purpose}
              >
                <TextArea
                  {...formik.getFieldProps("subject_purpose")}
                  width="20rem"
                  height={isOneGroupUser ? "7.5rem" : "6rem"}
                  maxlength="150"
                />
              </WithErrorForm>
            </StyledFormRow>
          </StyledVerticalArea>

          <StyledVerticalArea>
            <StyledFormRow>
              <Typography text="担当者1" />
              <WithErrorForm
                touched={formik.touched.main_user_id_group}
                errorMessage={formik.errors.main_user_id_group}
              >
                <SelectBox
                  name="main_user_id_group"
                  value={formik.values.main_user_id_group}
                  optionList={[
                    { value: "", text: "---" },
                    ...optionGroupUserList.filter((user) =>
                      isOneGroupUser
                        ? user
                        : user.optional === formik.values.group_id
                    ),
                  ]}
                  width="20rem"
                  onBlur={() =>
                    formik.setFieldTouched("main_user_id_group", true, true)
                  }
                  onChange={(e) => {
                    formik.setFieldValue("main_user_id_group", e.target.value);
                  }}
                  disabled={isOneGroupUser ? false : !formik.values.group_id}
                />
              </WithErrorForm>
            </StyledFormRow>

            <StyledFormRow>
              <Typography text="担当者2" />
              <SelectBox
                name="main_user_id_asmarq"
                value={formik.values.main_user_id_asmarq}
                optionList={[{ value: "", text: "---" }, ...asmarqUserList]}
                width="20rem"
                onChange={(e) => {
                  formik.setFieldValue("main_user_id_asmarq", e.target.value);
                }}
              />
            </StyledFormRow>

            <StyledFormRow>
              <Typography text="備考" />
              <WithErrorForm
                touched={formik.touched.memo}
                errorMessage={formik.errors.memo}
              >
                <TextArea
                  {...formik.getFieldProps("memo")}
                  width="20rem"
                  maxlength="150"
                  height="18.5rem"
                />
              </WithErrorForm>
            </StyledFormRow>
          </StyledVerticalArea>
        </StyledModalContent>

        <StyledModalFooter>
          <StyledButtonArea>
            {selectedSubject.id && (
              <Button
                text="削除"
                type="button"
                variant="secondary"
                onClick={clickDeleteButtonHandler}
              />
            )}
            <Button
              text={selectedSubject.id ? "更新" : "登録"}
              type="submit"
              variant={selectedSubject.id ? "primary" : "primary-reverse"}
            />
            {!selectedSubject.id && (
              <Button
                text="★スターをつけて登録"
                type="button"
                onClick={clickMarkedInsertButtonHandler}
              />
            )}
          </StyledButtonArea>
        </StyledModalFooter>
      </StyledForm>
    </Modal>
  );
};
